<template>
  <div class="chart-wrapper">
    <!-- 图表区域 -->
    <div class="chart-container">
      <v-chart :option="lineOption" autoresize />
    </div>

    <!-- 使用 a-card 和 a-card-grid 实现两行布局 -->
    <div class="card-grid-wrapper">
      <table class="school-cards-table">
        <thead>
          <tr>
            <th class="year-header">
              <h2>2025年</h2>
            </th>
            <th class="year-header">
              <h2>2030年</h2>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="year-column">
              <a-card :bordered="true" class="year-card">
                <a-row :gutter="16">
                  <a-col :span="6" v-for="item in year2025Data" :key="item.type">
                    <a-card class="school-type-card" :bordered="true" size="small">
                      <div class="grid-item" :class="item.className">
                        <div class="grid-item-content">
                          <div class="grid-icon">
                            <component
                              :is="item.icon"
                              :style="{ fontSize: '20px', color: item.color }"
                            />
                          </div>
                          <div class="grid-info">
                            <div class="grid-title">{{ item.title }}</div>
                            <div class="grid-value">{{ item.value }}</div>
                          </div>
                        </div>
                      </div>
                    </a-card>
                  </a-col>
                </a-row>
              </a-card>
            </td>
            <td class="year-column">
              <a-card :bordered="true" class="year-card">
                <a-row :gutter="16">
                  <a-col :span="6" v-for="item in year2030Data" :key="item.type">
                    <a-card class="school-type-card" :bordered="true" size="small">
                      <div class="grid-item" :class="item.className">
                        <div class="grid-item-content">
                          <div class="grid-icon">
                            <component
                              :is="item.icon"
                              :style="{ fontSize: '20px', color: item.color }"
                            />
                          </div>
                          <div class="grid-info">
                            <div class="grid-title">{{ item.title }}</div>
                            <div class="grid-value">{{ item.value }}</div>
                          </div>
                        </div>
                      </div>
                    </a-card>
                  </a-col>
                </a-row>
              </a-card>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  MarkPointComponent,
} from 'echarts/components';
import { IconHome, IconTool } from '@arco-design/web-vue/es/icon';

use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  MarkPointComponent,
]);

// 数据定义
const years = [
  '2010',
  '2011',
  '2012',
  '2013',
  '2014',
  '2015',
  '2016',
  '2017',
  '2018',
  '2019',
  '2020',
  '2021',
  '2022',
  '2023',
  '2024',
  '2025',
  '2026',
  '2027',
  '2028',
  '2029',
  '2030',
];

const populationData = [
  22935, 21284, 23263, 22163, 22896, 20043, 24716, 30536, 25829, 24013, 21470, 18458, 16691, 16281,
  16799, 16463, 16134, 15811, 15495, 15185, 14881,
];

// 找出最高点和最低点
const findExtremePoints = () => {
  let maxIndex = 0;
  let minIndex = 0;
  let maxValue = populationData[0];
  let minValue = populationData[0];

  populationData.forEach((value, index) => {
    if (value > maxValue) {
      maxValue = value;
      maxIndex = index;
    }
    if (value < minValue) {
      minValue = value;
      minIndex = index;
    }
  });

  return {
    max: {
      year: years[maxIndex],
      value: maxValue,
      index: maxIndex,
    },
    min: {
      year: years[minIndex],
      value: minValue,
      index: minIndex,
    },
  };
};

const extremePoints = findExtremePoints();

const lineOption = ref({
  title: {
    text: '玉溪市2010—2030年出生人口统计及预测',
    left: 'center',
    textStyle: {
      fontSize: 18,
      fontWeight: 'bold',
    },
    subtextStyle: {
      fontSize: 14,
      color: '#666',
    },
  },
  tooltip: {
    trigger: 'axis',
    formatter: function (params) {
      const data = params[0];
      const year = data.name;
      const value = data.value.toLocaleString();
      const yearIndex = years.indexOf(year);

      let extraInfo = '';
      if (yearIndex === extremePoints.max.index) {
        extraInfo = '<br/><span style="color:#d4380d;font-weight:bold;">※ 最高点</span>';
      } else if (yearIndex === extremePoints.min.index) {
        extraInfo = '<br/><span style="color:#52c41a;font-weight:bold;">※ 最低点</span>';
      }

      return `${year}年<br/>人口数量: ${value}人${extraInfo}`;
    },
  },
  legend: {
    data: ['人口数量'],
    top: '15%',
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '10%',
    containLabel: true,
  },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: years,
    axisLabel: {
      rotate: 45,
      interval: 0,
    },
  },
  yAxis: {
    type: 'value',
    name: '人口数量(人)',
    nameLocation: 'middle',
    nameGap: 40,
    axisLabel: {
      formatter: '{value}',
    },
  },
  series: [
    {
      name: '人口数量',
      type: 'line',
      smooth: true,
      data: populationData,
      itemStyle: {
        color: '#1890ff',
      },
      lineStyle: {
        width: 3,
      },
      markPoint: {
        data: [
          {
            name: '最高点',
            coord: [extremePoints.max.index, extremePoints.max.value],
            value: extremePoints.max.value,
            symbol: 'pin',
            symbolSize: 50,
            itemStyle: {
              color: '#d4380d',
            },
            label: {
              formatter: function (params) {
                return `最高\n${
                  extremePoints.max.year
                }年\n${extremePoints.max.value.toLocaleString()}人`;
              },
              color: '#fff',
              backgroundColor: '#d4380d',
              padding: [5, 10],
              borderRadius: 4,
            },
          },
          {
            name: '最低点',
            coord: [extremePoints.min.index, extremePoints.min.value],
            value: extremePoints.min.value,
            symbol: 'pin',
            symbolSize: 50,
            itemStyle: {
              color: '#52c41a',
            },
            label: {
              formatter: function (params) {
                return `最低\n${
                  extremePoints.min.year
                }年\n${extremePoints.min.value.toLocaleString()}人`;
              },
              color: '#fff',
              backgroundColor: '#52c41a',
              padding: [5, 10],
              borderRadius: 4,
            },
          },
        ],
        symbol: 'circle',
        symbolSize: 8,
        label: {
          show: true,
          position: 'top',
          color: '#fff',
          fontWeight: 'bold',
        },
      },
      markLine: {
        silent: true,
        data: [
          {
            yAxis: extremePoints.max.value,
            lineStyle: {
              color: '#d4380d',
              type: 'dashed',
              width: 1,
            },
            label: {
              formatter: `最高: ${extremePoints.max.value.toLocaleString()}人`,
              position: 'end',
              color: '#d4380d',
            },
          },
          {
            yAxis: extremePoints.min.value,
            lineStyle: {
              color: '#52c41a',
              type: 'dashed',
              width: 1,
            },
            label: {
              formatter: `最低: ${extremePoints.min.value.toLocaleString()}人`,
              position: 'end',
              color: '#52c41a',
            },
          },
        ],
      },
    },
  ],
});

const year2025Data = ref([
  {
    type: 'primary',
    title: '小学',
    value: '399',
    color: '#1890ff',
    icon: IconHome,
    className: 'school-item',
  },
  {
    type: 'middle',
    title: '中学',
    value: '85',
    color: '#52c41a',
    icon: IconHome,
    className: 'middle-school-item',
  },
  {
    type: 'high',
    title: '高中',
    value: '22',
    color: '#722ed1',
    icon: IconHome,
    className: 'high-school-item',
  },
  {
    type: 'vocational',
    title: '职中',
    value: '13',
    color: '#fa8c16',
    icon: IconTool,
    className: 'vocational-item',
  },
]);

// 2030年数据
const year2030Data = ref([
  {
    type: 'primary',
    title: '小学',
    value: '315',
    color: '#1890ff',
    icon: IconHome,
    className: 'school-item',
  },
  {
    type: 'middle',
    title: '中学',
    value: '76',
    color: '#52c41a',
    icon: IconHome,
    className: 'middle-school-item',
  },
  {
    type: 'high',
    title: '高中',
    value: '23',
    color: '#722ed1',
    icon: IconHome,
    className: 'high-school-item',
  },
  {
    type: 'vocational',
    title: '职中',
    value: '12',
    color: '#fa8c16',
    icon: IconTool,
    className: 'vocational-item',
  },
]);
</script>

<style scoped>
.chart-wrapper {
  width: 100%;
  height: 100%;
  min-height: 700px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 图表容器 - 增加高度 */
.chart-container {
  flex: 1;
  min-height: 400px;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.chart-container :deep(.echarts) {
  width: 100% !important;
  height: 100% !important;
}

/* 卡片网格包装器 */
.card-grid-wrapper {
  height: 320px;
}

/* 主卡片样式 */
.main-card {
  height: 100%;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.main-card :deep(.arco-card-header) {
  border-bottom: 1px solid #e8e8e8;
  padding: 16px 20px;
  height: 60px;
}

.main-card :deep(.arco-card-header-title) {
  font-size: 18px;
  font-weight: 600;
  color: #1d2129;
  display: flex;
  align-items: center;
}

.main-card :deep(.arco-card-body) {
  padding: 20px;
  height: calc(100% - 60px);
  display: flex;
  flex-direction: column;
}

.year-title {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
}

.year-subtitle {
  font-size: 12px;
  color: #86909c;
  background: rgba(134, 144, 156, 0.1);
  padding: 2px 8px;
  border-radius: 10px;
}

/* 卡片网格行 */
.card-grid-row {
  display: grid !important;
  grid-template-columns: repeat(4, 1fr) !important;
  gap: 12px !important;
  margin-bottom: 20px !important;
  padding: 0 !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

/* 网格项 */
.grid-item {
  background: white;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #f0f0f0;
  transition: all 0.3s ease;
  cursor: pointer;
}

.grid-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

/* 第一行和第二行的不同边框颜色 */
.first-row .grid-item {
  border-top: 3px solid;
}

.second-row .grid-item {
  border-top: 3px solid;
}

/* 各类型颜色 */
.school-item {
  border-top-color: #1890ff !important;
}

.middle-school-item {
  border-top-color: #52c41a !important;
}

.high-school-item {
  border-top-color: #722ed1 !important;
}

.vocational-item {
  border-top-color: #fa8c16 !important;
}

/* 网格项内容 */
.grid-item-content {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 100%;
}

.grid-icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.school-item .grid-icon {
  background: rgba(24, 144, 255, 0.1);
}

.middle-school-item .grid-icon {
  background: rgba(82, 196, 26, 0.1);
}

.high-school-item .grid-icon {
  background: rgba(114, 46, 209, 0.1);
}

.vocational-item .grid-icon {
  background: rgba(250, 140, 22, 0.1);
}

.grid-info {
  flex: 1;
}

.grid-title {
  font-size: 14px;
  color: #86909c;
  margin-bottom: 4px;
  font-weight: 500;
}

.grid-value {
  font-size: 22px;
  font-weight: 700;
  color: #1d2129;
  line-height: 1.2;
}

/* 变化指示器 */
.grid-change {
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
  margin-top: 4px;
  display: inline-block;
}

.grid-change.positive {
  color: #00b42a;
  background: rgba(0, 180, 42, 0.1);
}

.grid-change.negative {
  color: #f53f3f;
  background: rgba(245, 63, 63, 0.1);
}

/* 数据总结 */
.data-summary {
  margin-top: auto;
  padding: 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-label {
  font-size: 14px;
  color: #86909c;
}

.summary-value {
  font-size: 18px;
  font-weight: 600;
  color: #1d2129;
}

.summary-change {
  font-size: 12px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
}

.summary-change.positive {
  color: #00b42a;
  background: rgba(0, 180, 42, 0.1);
}

.summary-change.negative {
  color: #f53f3f;
  background: rgba(245, 63, 63, 0.1);
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .card-grid-row {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  .card-grid-wrapper {
    height: 480px;
  }
}

@media (max-width: 768px) {
  .card-grid-row {
    grid-template-columns: 1fr !important;
  }

  .card-grid-wrapper {
    height: 640px;
  }

  .data-summary {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .chart-wrapper {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .grid-value {
    font-size: 20px;
  }

  .grid-icon {
    width: 36px;
    height: 36px;
  }
}

.card-grid-wrapper {
  margin: 20px 0;
}

.school-cards-table {
  width: 100%;
  border-collapse: collapse;
}

.school-cards-table th,
.school-cards-table td {
  border: 1px solid #62f4a6;
  padding: 0;
}
/* .school-cards-table th {
  background-color: #314353;
} */

.year-header {
  text-align: center;
  padding: 16px;
  background-color: #b1cbf4;
  line-height: 40px;
}

.year-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #f3f4f5;
}

.year-column {
  width: 50%;
  vertical-align: top;
}

.year-card {
  margin: 0;
  border-radius: 0;
  box-shadow: none;
}

.year-card :deep(.arco-card-body) {
  padding: 20px;
}

.school-type-card {
  border-radius: 4px;
  transition: all 0.3s ease;
}

.school-type-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.school-type-card :deep(.arco-card-body) {
  padding: 16px;
}

.grid-item {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.grid-item-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
}

.grid-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(24, 144, 255, 0.1);
  border-radius: 50%;
  margin-bottom: 4px;
}

.school-item .grid-icon {
  background-color: rgba(24, 144, 255, 0.1);
}

.middle-school-item .grid-icon {
  background-color: rgba(82, 196, 26, 0.1);
}

.high-school-item .grid-icon {
  background-color: rgba(114, 46, 209, 0.1);
}

.vocational-item .grid-icon {
  background-color: rgba(250, 140, 22, 0.1);
}

.grid-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.grid-title {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.grid-value {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.school-item .grid-value {
  color: #1890ff;
}

.middle-school-item .grid-value {
  color: #52c41a;
}

.high-school-item .grid-value {
  color: #722ed1;
}

.vocational-item .grid-value {
  color: #fa8c16;
}

@media (max-width: 768px) {
  .school-cards-table {
    display: block;
  }

  .school-cards-table thead,
  .school-cards-table tbody,
  .school-cards-table th,
  .school-cards-table td,
  .school-cards-table tr {
    display: block;
  }

  .year-header {
    width: 100% !important;
  }

  .year-column {
    width: 100% !important;
  }

  .year-card :deep(.arco-card-body) {
    padding: 12px;
  }

  .school-type-card :deep(.arco-card-body) {
    padding: 12px;
  }
}
</style>
